import { getInterval, addDays, fmtShort, getBuilding, getStatus } from './utils'

const STYLES = `
  body{font-family:system-ui,sans-serif;margin:0;padding:16px;color:#1e2a3a;font-size:12px}
  h1{font-size:16px;font-weight:800;color:#0f2237;margin:0 0 4px}
  .subtitle{font-size:11px;color:#6b7c93;margin-bottom:16px}
  table{width:100%;border-collapse:collapse;margin-bottom:16px}
  th{background:#0f2237;color:#fff;padding:8px 10px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;text-align:left}
  td{padding:8px 10px;border-bottom:1px solid #e2e8f0;font-size:11px}
  tr:nth-child(even){background:#f8fafc}
  .badge{display:inline-block;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700}
  .overdue{background:#fdecea;color:#c0392b}
  .due-soon{background:#fef0e6;color:#d35400}
  .upcoming{background:#fefae6;color:#b7860b}
  .ok{background:#eafaf1;color:#1e8449}
  .nodate{background:#f0f2f5;color:#6b7c93}
  .header{background:#0f2237;color:#fff;padding:14px 18px;margin:-16px -16px 16px;display:flex;justify-content:space-between;align-items:center}
  .header h1{color:#fff;margin:0;font-size:18px}
  .header .date{font-size:11px;color:#5a8ab0}
  .section-title{background:#1a3d5c;color:#fff;padding:6px 12px;font-size:11px;font-weight:700;text-transform:uppercase;margin:12px 0 0;border-radius:4px 4px 0 0}
  @media print{@page{margin:12mm}body{padding:0}.no-print{display:none}}
`

function printHTML(html) {
  const w = window.open('','_blank')
  w.document.write(`<!DOCTYPE html><html><head><style>${STYLES}</style></head><body>${html}<script>window.onload=()=>setTimeout(()=>window.print(),600)</script></body></html>`)
  w.document.close()
}

// Print unit list
export function printUnitList(units, season) {
  const byBuilding = {}
  units.forEach(u=>{
    const b=getBuilding(u.area)
    if(!byBuilding[b]) byBuilding[b]=[]
    byBuilding[b].push(u)
  })

  const sections = Object.entries(byBuilding).map(([building,bUnits])=>`
    <div class="section-title">🏢 ${building}</div>
    <table>
      <thead><tr><th>Location</th><th>Description</th><th>Filter Size</th><th>Qty</th><th>Summer</th><th>Winter</th><th>Type</th></tr></thead>
      <tbody>${bUnits.map(u=>`
        <tr>
          <td><strong>${u.location}</strong></td>
          <td style="color:#6b7c93">${u.description||'—'}</td>
          <td style="font-family:monospace;font-weight:700;color:#0f2237">${u.filter_type==='washable'?'Washable':u.size}</td>
          <td style="text-align:center">${u.qty}</td>
          <td style="text-align:center">${u.summer_interval}d</td>
          <td style="text-align:center">${u.winter_interval?`${u.winter_interval}d`:'OFF'}</td>
          <td>${u.filter_type==='washable'?'♻️ Washable':'Standard'}</td>
        </tr>`).join('')}
      </tbody>
    </table>`).join('')

  const date = new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})
  printHTML(`
    <div class="header"><h1>⚙ HVAC Filter Manager — Unit List</h1><div class="date">${date} · ${units.length} units · ${season==='summer'?'☀️ Summer':'❄️ Winter'}</div></div>
    ${sections}
  `)
}

// Print to-do list
export function printTodoList(units, changelog, season, period='today', worker='all', users=[]) {
  const today = new Date(); today.setHours(0,0,0,0)
  const endDate = period==='today' ? today : addDays(today,6)

  const todoUnits = units.filter(u=>{
    const iv=getInterval(u,season)
    if(!iv) return false
    if(worker!=='all'&&u.assigned_to!==worker) return false
    const status=getStatus(u,season)
    if(status.label==='OVERDUE') return true
    if(status.label==='NO DATE') return period==='week'
    if(!u.last_changed) return false
    const due=addDays(new Date(u.last_changed),iv)
    if(period==='today') return due<=today
    return due>=today&&due<=endDate
  }).sort((a,b)=>{
    const sa=getStatus(a,season),sb=getStatus(b,season)
    return (sa.days??-9999)-(sb.days??-9999)
  })

  const workerName = worker==='all'?'All Workers':users.find(u=>u.id===worker)?.name||'Unknown'
  const periodLabel = period==='today'?'Today':period==='week'?'This Week':'All'
  const date = new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})

  const rows = todoUnits.map(u=>{
    const status=getStatus(u,season)
    const statusClass=status.label==='OVERDUE'?'overdue':status.label==='DUE SOON'?'due-soon':status.label==='UPCOMING'?'upcoming':status.label==='NO DATE'?'nodate':'ok'
    const assignedUser=users.find(usr=>usr.id===u.assigned_to)
    return `<tr>
      <td style="width:24px"><input type="checkbox" style="width:16px;height:16px"/></td>
      <td><strong>${u.location}</strong><br/><span style="color:#6b7c93;font-size:10px">${u.area}${u.description?` · ${u.description}`:''}</span></td>
      <td style="font-family:monospace;font-weight:700;color:#0f2237">${u.filter_type==='washable'?'♻️':u.size}</td>
      <td style="text-align:center">${u.qty}</td>
      <td><span class="badge ${statusClass}">${status.days!==null?(status.days<0?`${Math.abs(status.days)}d overdue`:status.days===0?'Today':`${status.days}d left`):'No date'}</span></td>
      <td style="color:#6b7c93">${assignedUser?.name||'Unassigned'}</td>
      <td style="width:80px"></td>
    </tr>`
  }).join('')

  printHTML(`
    <div class="header">
      <h1>⚙ HVAC To-Do List</h1>
      <div class="date">${date}</div>
    </div>
    <table style="margin-bottom:8px;border:none">
      <tr style="background:transparent">
        <td style="border:none;padding:4px 0;color:#6b7c93">Period: <strong style="color:#0f2237">${periodLabel}</strong></td>
        <td style="border:none;padding:4px 0;color:#6b7c93">Worker: <strong style="color:#0f2237">${workerName}</strong></td>
        <td style="border:none;padding:4px 0;color:#6b7c93">Total: <strong style="color:#0f2237">${todoUnits.length} units</strong></td>
      </tr>
    </table>
    ${todoUnits.length>0?`
    <table>
      <thead><tr><th></th><th>Unit</th><th>Filter</th><th>Qty</th><th>Status</th><th>Assigned</th><th>Done ✓</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div style="margin-top:16px;padding:12px;border:1px solid #e2e8f0;border-radius:8px;font-size:11px;color:#6b7c93">
      Notes: _____________________________________________________________________________________________________________
    </div>`
    :'<div style="text-align:center;padding:32px;color:#6b7c93">No units due for this period.</div>'}
  `)
}

// Print inventory count sheet
export function printInventorySheet(inventory, units) {
  const date = new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})
  
  const rows = inventory.map(item=>{
    const usedBy = units.filter(u=>u.size===item.size&&u.filter_type!=='washable')
    return `<tr>
      <td style="font-family:monospace;font-weight:700;font-size:13px;color:#0f2237">${item.size}</td>
      <td style="text-align:center;color:#6b7c93">${item.min_stock}</td>
      <td style="font-size:10px;color:#6b7c93">${usedBy.slice(0,3).map(u=>u.location).join(', ')}${usedBy.length>3?` +${usedBy.length-3} more`:''}</td>
      <td style="width:80px;border:1px solid #ccc;border-radius:4px">&nbsp;</td>
      <td style="width:120px;border:1px solid #ccc;border-radius:4px">&nbsp;</td>
    </tr>`
  }).join('')

  printHTML(`
    <div class="header"><h1>⚙ HVAC Filter Manager — Inventory Count Sheet</h1><div class="date">${date}</div></div>
    <p style="font-size:11px;color:#6b7c93;margin-bottom:16px">Please count all filter stock and fill in the actual count. Return this sheet to the manager to update the system.</p>
    <table>
      <thead><tr>
        <th>Filter Size</th>
        <th style="text-align:center">Min Required</th>
        <th>Used In</th>
        <th style="text-align:center">Actual Count</th>
        <th>Notes</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div style="margin-top:20px;padding:12px;border:1px solid #e2e8f0;border-radius:8px;font-size:11px;color:#6b7c93">
      Counted by: _________________________________ &nbsp;&nbsp; Date: _________________ &nbsp;&nbsp; Verified by: _________________________________
    </div>
  `)
}

// Print change log
export function printChangeLog(entries, dateFrom, dateTo) {
  const date = new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})
  const rows = entries.map(l=>`
    <tr>
      <td style="white-space:nowrap">${fmtShort(l.change_date)}</td>
      <td style="color:#6b7c93">${l.area}</td>
      <td><strong>${l.location}</strong></td>
      <td style="font-family:monospace;font-weight:700;color:#0f2237">${l.size}</td>
      <td style="text-align:center">${l.qty_used}</td>
      <td>${l.worker_name}</td>
      <td>${l.condition||'—'}</td>
      <td style="color:#6b7c93;font-size:10px">${l.notes||''}</td>
    </tr>`).join('')

  printHTML(`
    <div class="header"><h1>⚙ HVAC Change Log</h1><div class="date">${date} · ${entries.length} entries${dateFrom?` · From ${fmtShort(dateFrom)}`:''} ${dateTo?`to ${fmtShort(dateTo)}`:''}</div></div>
    <table>
      <thead><tr><th>Date</th><th>Area</th><th>Location</th><th>Filter</th><th>Qty</th><th>Done By</th><th>Condition</th><th>Notes</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  `)
}
